import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { useAuth, useDictionary, usePage } from '@/src/app/hooks';
import { Container, type ResourceHeaderDTO } from '@/src/ui';
import { apiRequest } from '@/src/services';
import { type TextbookItemDTO } from './DTOs';
import './EBooksPage.css';

import gTKCover from '@/src/assets/img/lessons/cover-grade-TK.jpg';
import gKCover from '@/src/assets/img/lessons/cover-grade-K.jpg';
import g1Cover from '@/src/assets/img/lessons/cover-grade-1.jpg';
import g2Cover from '@/src/assets/img/lessons/cover-grade-2.jpg';
import g3Cover from '@/src/assets/img/lessons/cover-grade-3.jpg';
import g4Cover from '@/src/assets/img/lessons/cover-grade-4.jpg';
import g5Cover from '@/src/assets/img/lessons/cover-grade-5.jpg';
import g6Cover from '@/src/assets/img/lessons/cover-grade-6.jpg';
import g6MCover from '@/src/assets/img/lessons/cover-grade-6M.jpg';
import g7Cover from '@/src/assets/img/lessons/cover-grade-7.jpg';
import g8Cover from '@/src/assets/img/lessons/cover-grade-8.jpg';

const EBooksPage: React.FC = () => {
    const { setSubheaderTitle, setSubheaderType, pageLanguage } = usePage();
    const { user } = useAuth();
    const { translate } = useDictionary();
    document.title = translate('eBooks', 'eBooks');

    useEffect(() => {
        setSubheaderTitle(translate('eBooks'));
        setSubheaderType('default');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translate]);

    const [btbfGuideLinks, setBtbfGuideLinks] = useState<Record<string, string>>({});

    const fetchBtbf = useCallback(async () => {
        try {
            const res = await apiRequest<ResourceHeaderDTO[]>('GET', 'dbo/btbf');
            const guideBox = res.data.find((box) => box.title === 'BTBF Guide');
            if (!guideBox) return;

            const linkMap: Record<string, string> = {};
            for (const item of guideBox.items) {
                if (!item.link) continue;
                const text = item.text.toLowerCase();
                if (text.includes('grade k') || text.includes('k')) linkMap['K'] = item.link;
                else if (text.includes('grade 1') || text === '1') linkMap['1'] = item.link;
                else if (text.includes('grade 2') || text === '2') linkMap['2'] = item.link;
                else if (text.includes('grade 3') || text === '3') linkMap['3'] = item.link;
                else if (text.includes('upper')) {
                    for (const g of ['4', '5', '6', '6M', '7', '8']) linkMap[g] = item.link;
                }
            }
            setBtbfGuideLinks(linkMap);
        } catch {
            // silently ignore
        }
    }, []);

    useEffect(() => {
        fetchBtbf();
    }, [fetchBtbf]);

    const assignedBooks = useMemo(() => {
        if (!user?.allowedGrades) return [];

        const elementaryGrades = user.allowedGrades.elementary || [];
        const middleSchoolGrades = user.allowedGrades.middleSchool || [];

        return [...elementaryGrades, ...middleSchoolGrades].map((g) => {
            const gradeObj: TextbookItemDTO = {
                image: `/Content/assets/img/lessons/cover-grade-${g}.jpg`,
                code: g,
                viewAllLessonsLink: `/lessons/${g}`,
                name: translate('Grade') + ' ' + g,
            };

            if (g === 'TK') {
                gradeObj.teLink1 = {
                    displayText: 'Vol CC',
                    link: `https://teacher-files.paradigmmath.com/EN/_flipbook/TE/G${g}-TE-Vol-CC/index.html`,
                };
                gradeObj.teLink2 = {
                    displayText: 'Vol OA',
                    link: `https://teacher-files.paradigmmath.com/EN/_flipbook/TE/G${g}-TE-Vol-OA/index.html`,
                };
                gradeObj.teLink3 = {
                    displayText: 'Vol MD',
                    link: `https://teacher-files.paradigmmath.com/EN/_flipbook/TE/G${g}-TE-Vol-MD/index.html`,
                };
                gradeObj.teLink4 = {
                    displayText: 'Vol G',
                    link: `https://teacher-files.paradigmmath.com/EN/_flipbook/TE/G${g}-TE-Vol-G/index.html`,
                };
                gradeObj.seLink1 = {
                    displayText: 'Resources',
                    link: `https://teacher-files.paradigmmath.com/EN/_flipbook/SE/G${g}-SE-Resources/index.html`,
                };
            } else if (g === 'K') {
                gradeObj.teLink1 = {
                    displayText: 'Vol 1',
                    link: `https://teacher-files.paradigmmath.com/EN/_flipbook/TE/G${g}-TE-Vol-1/index.html`,
                };
                gradeObj.teLink2 = {
                    displayText: 'Vol 2',
                    link: `https://teacher-files.paradigmmath.com/EN/_flipbook/TE/G${g}-TE-Vol-2/index.html`,
                };
                gradeObj.seLink1 = {
                    displayText: 'Vol 1',
                    link: `https://teacher-files.paradigmmath.com/EN/_flipbook/SE/G${g}-SE-Vol-1/index.html`,
                };
                gradeObj.seLink2 = {
                    displayText: 'Vol 2',
                    link: `https://teacher-files.paradigmmath.com/EN/_flipbook/SE/G${g}-SE-Vol-2/index.html`,
                };
            } else {
                gradeObj.teLink1 = {
                    displayText: 'Vol 1A',
                    link: `https://teacher-files.paradigmmath.com/EN/_flipbook/TE/G${g}-TE-Vol-1A/index.html`,
                };
                gradeObj.teLink2 = {
                    displayText: 'Vol 1B',
                    link: `https://teacher-files.paradigmmath.com/EN/_flipbook/TE/G${g}-TE-Vol-1B/index.html`,
                };
                gradeObj.teLink3 = {
                    displayText: 'Vol 2A',
                    link: `https://teacher-files.paradigmmath.com/EN/_flipbook/TE/G${g}-TE-Vol-2A/index.html`,
                };
                gradeObj.teLink4 = {
                    displayText: 'Vol 2B',
                    link: `https://teacher-files.paradigmmath.com/EN/_flipbook/TE/G${g}-TE-Vol-2B/index.html`,
                };
                gradeObj.seLink1 = {
                    displayText: 'Vol 1',
                    link: `https://teacher-files.paradigmmath.com/EN/_flipbook/SE/G${g}-SE-Vol-1/index.html`,
                };
                gradeObj.seLink2 = {
                    displayText: 'Vol 2',
                    link: `https://teacher-files.paradigmmath.com/EN/_flipbook/SE/G${g}-SE-Vol-2/index.html`,
                };
            }

            return gradeObj;
        });
    }, [user?.allowedGrades, translate, pageLanguage]);

    return (
        <Container>
            <div className="lesson-grades-container">
                <div id="lesson-grades-container">
                    {assignedBooks.map((grade) => (
                        <div key={grade.code} className="grade-card">
                            <h2 className="grade-names pd-primary text">{grade.name}</h2>

                            <img src={getGradeCover(grade.code)} alt={grade.name} />

                            <div className="ebook-buttons">
                                <div className="ebook-section-label">
                                    {translate('Teacher Edition')}
                                </div>
                                <div className="ebook-link-group">
                                    {grade.teLink1 && (
                                        <a
                                            className={`ebook-btn pd-G${grade.code}-primary background`}
                                            href={grade.teLink1.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {grade.teLink1.displayText}
                                        </a>
                                    )}
                                    {grade.teLink2 && (
                                        <a
                                            className={`ebook-btn pd-G${grade.code}-primary background`}
                                            href={grade.teLink2.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {grade.teLink2.displayText}
                                        </a>
                                    )}
                                    {grade.teLink3 && (
                                        <a
                                            className={`ebook-btn pd-G${grade.code}-primary background`}
                                            href={grade.teLink3.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {grade.teLink3.displayText}
                                        </a>
                                    )}
                                    {grade.teLink4 && (
                                        <a
                                            className={`ebook-btn pd-G${grade.code}-primary background`}
                                            href={grade.teLink4.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {grade.teLink4.displayText}
                                        </a>
                                    )}
                                </div>

                                <div className="ebook-section-label">
                                    {translate('Student Edition')}
                                </div>
                                <div className="ebook-link-group">
                                    {grade.seLink1 && (
                                        <a
                                            className={`ebook-btn pd-G${grade.code}-primary background`}
                                            href={grade.seLink1.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {grade.seLink1.displayText}
                                        </a>
                                    )}
                                    {grade.seLink2 && (
                                        <a
                                            className={`ebook-btn pd-G${grade.code}-primary background`}
                                            href={grade.seLink2.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {grade.seLink2.displayText}
                                        </a>
                                    )}
                                </div>

                                {btbfGuideLinks[grade.code] && (
                                    <div
                                        className="ebook-link-group single"
                                        style={{ marginTop: '10px' }}
                                    >
                                        <a
                                            className="ebook-btn ebook-btn-navy"
                                            href={btbfGuideLinks[grade.code]}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {translate('Beyond the Basic Facts')}
                                        </a>
                                    </div>
                                )}
                            </div>

                            <Link
                                to={grade.viewAllLessonsLink}
                                className="view-lessons-link ebook-btn-navy"
                            >
                                {translate('View All Lessons')}
                            </Link>
                        </div>
                    ))}
                </div>
            </div>
        </Container>
    );
};

export default EBooksPage;

function getGradeCover(grade: string): string {
    switch (grade) {
        case 'TK':
            return gTKCover;
        case 'K':
            return gKCover;
        case '1':
            return g1Cover;
        case '2':
            return g2Cover;
        case '3':
            return g3Cover;
        case '4':
            return g4Cover;
        case '5':
            return g5Cover;
        case '6':
            return g6Cover;
        case '6M':
            return g6MCover;
        case '7':
            return g7Cover;
        case '8':
            return g8Cover;
        default:
            return '';
    }
}
